import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Lock, ShieldCheck } from 'lucide-react';

const ACCESS_CODE = 'payhub2026';
const STORAGE_KEY = 'payhub_site_access';

export default function SiteAccessGate({ children }) {
  const [hasAccess, setHasAccess] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user already has access
    const storedAccess = localStorage.getItem(STORAGE_KEY);
    if (storedAccess === 'granted') {
      setHasAccess(true);
    }
    setLoading(false);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (code.toLowerCase() === ACCESS_CODE.toLowerCase()) {
      localStorage.setItem(STORAGE_KEY, 'granted');
      setHasAccess(true);
    } else {
      setError('Code d\'accès invalide / Invalid access code');
      setCode('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#003DA5] to-[#001d52]">
        <div className="animate-spin w-8 h-8 border-4 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (hasAccess) {
    return children;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#003DA5] to-[#001d52] p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur rounded-2xl mb-4">
            <img 
              src="/quebec-plus-icon.png" 
              alt="Q+" 
              className="h-12 w-12 object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-white">PayHub Québec<sup>+</sup></h1>
          <p className="text-white/60 mt-2">Accès restreint / Restricted Access</p>
        </div>

        {/* Access Form */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-white/10 rounded-lg">
              <Lock className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Site en développement</h2>
              <p className="text-sm text-white/60">Entrez le code d'accès pour continuer</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="password"
                placeholder="Code d'accès / Access code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-white/40"
                autoFocus
                data-testid="site-access-code"
              />
            </div>

            {error && (
              <p className="text-red-300 text-sm text-center">{error}</p>
            )}

            <Button
              type="submit"
              className="w-full h-12 bg-white text-[#003DA5] hover:bg-white/90 font-semibold"
              data-testid="site-access-submit"
            >
              <ShieldCheck className="h-5 w-5 mr-2" />
              Accéder au site
            </Button>
          </form>

          <p className="text-center text-white/40 text-xs mt-6">
            © 2026 PayHub.Québec<sup>+</sup>
          </p>
        </div>

        {/* Beta Badge */}
        <div className="text-center mt-6">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white/80 text-sm">
            <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
            Version Beta - Accès limité
          </span>
        </div>
      </div>
    </div>
  );
}
